import { useMemo } from "react";
import {
  BatchItem,
  BatchItemStatus,
  PayableImportBatchItemData
} from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

const HEADERS = [
  { label: "Contractor engagement ID", key: "payeeEngagementId" },
  { label: "Payment reference ID", key: "uniqueReferenceKey" },
  { label: "Line item title", key: "description" },
  { label: "Line item description", key: "detail" },
  { label: "Amount", key: "totalCost" },
  { label: "Due date", key: "dueDate" },
  { label: "Pay date", key: "paidDate" },
  { label: "Invoice notes", key: "notes" },
  { label: "Error", key: "error" }
];

/**
 * The rows v3 could not import, as a CSV the user can correct and re-upload.
 *
 * v1 reconstructed the original spreadsheet columns from the batch item's
 * labels; v3 stores the item as the typed `PayableImportBatchItemData` it was
 * posted as, so the export mirrors that shape plus `statusReason`.
 */
export function useDownloadPayableImportErrorsCSV(batchId: string) {
  const queryFailedItems = queriesBatches.listBatchItems.useQueryAll({
    batchId,
    filter: { status: { anyOf: [BatchItemStatus.FAILED] } }
  });

  const failedItems = useMemo(
    () => queryFailedItems.flatData ?? [],
    [queryFailedItems.flatData]
  );

  const errorsCSVFileProps = useMemo(() => {
    const data = failedItems.map((batchItem: BatchItem) => {
      const itemData = batchItem.data as PayableImportBatchItemData;

      return {
        payeeEngagementId: itemData?.payeeEngagementId ?? "",
        uniqueReferenceKey: itemData?.uniqueReferenceKey ?? "",
        description: itemData?.description ?? "",
        detail: itemData?.detail ?? "",
        totalCost: itemData?.totalCost ?? "",
        dueDate: itemData?.dueDate ? itemData.dueDate.toISOString() : "",
        paidDate: itemData?.paidDate ? itemData.paidDate.toISOString() : "",
        notes: itemData?.notes ?? "",
        error: batchItem.statusReason ?? ""
      };
    });

    return {
      filename: `import-errors-${batchId}.csv`,
      headers: HEADERS,
      data
    };
  }, [failedItems, batchId]);

  return { errorsCSVFileProps, hasErrors: failedItems.length > 0 };
}
